import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { translate } from '../../../i18n';
import { isBrowserOffline } from '../../../utils/browserOnline';
import { OFFLINE_NO_DATA_I18N_KEY } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useVenueDisplayState, VenueCourtState, VenueCurrentState } from '../hooks/useVenueDisplayState';
import './venue-display-live.css';

const PAGE_CAPACITY = 6;
const STALE_MS = 60_000;

function formatTime(d: Date): string {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}
function formatClock(d: Date): string {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}
function formatDay(d: Date): string {
    return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}
function minutesUntil(target: Date, now: Date): number {
    return Math.max(0, Math.round((target.getTime() - now.getTime()) / 60_000));
}
function chunk<T>(arr: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

function buildPages(courts: VenueCourtState[]): VenueCourtState[][] {
    if (courts.length <= PAGE_CAPACITY) return [courts];
    const sticky = courts.filter((c) => c.sticky);
    const rest = courts.filter((c) => !c.sticky);
    if (sticky.length === 0) return chunk(rest, PAGE_CAPACITY);
    const slotsPerPage = Math.max(1, PAGE_CAPACITY - sticky.length);
    const restPages = chunk(rest, slotsPerPage);
    return restPages.map((page) => [...sticky, ...page]);
}

/* ---- Row rendering ---- */

function CurrentBlock({ state }: { state: VenueCurrentState }) {
    if (state.kind === 'live') {
        return (
            <div className="vdl-match">
                <div className="vdl-dot" />
                <div className="vdl-pair">{state.players}</div>
            </div>
        );
    }
    if (state.kind === 'class') {
        return (
            <div className="vdl-match">
                <div className="vdl-dot" />
                <div className="vdl-pair-open">
                    <span className="accent">{translate('venue_display.live.kind_class')}</span>{' · '}
                    {translate('venue_display.live.class_with_coach', { name: state.coachName })}
                </div>
            </div>
        );
    }
    if (state.kind === 'event') {
        return (
            <div className="vdl-match">
                <div className="vdl-dot" />
                <div className="vdl-pair-open"><span className="accent">{state.name}</span></div>
            </div>
        );
    }
    if (state.kind === 'blocked') {
        return (
            <div className="vdl-match">
                <div className="vdl-dot idle" />
                <div className="vdl-pair-blocked">
                    {state.reasonLabel} · {translate('venue_display.live.reopens_at', { time: formatTime(state.endsAt) })}
                </div>
            </div>
        );
    }
    if (state.kind === 'open') {
        return (
            <div className="vdl-match">
                <div className="vdl-dot open" />
                <div className="vdl-pair-open">
                    <span className="accent">{translate('venue_display.live.open_booking')}</span> · {translate('venue_display.live.available')}
                </div>
            </div>
        );
    }
    return (
        <div className="vdl-match">
            <div className="vdl-dot idle" />
            <div className="vdl-pair-blocked">{translate('venue_display.live.no_sessions')}</div>
        </div>
    );
}

function TimeBlock({ state, next, now }: { state: VenueCurrentState; next: VenueCourtState['next']; now: Date }) {
    if (state.kind === 'live' || state.kind === 'class' || state.kind === 'event') {
        const minLeft = minutesUntil(state.endsAt, now);
        const sportLabel = state.kind === 'live'
            ? (state.sport === 'singles' ? translate('venue_display.live.sport_singles')
                : state.sport === 'open' ? translate('venue_display.live.sport_open')
                    : translate('venue_display.live.sport_doubles'))
            : state.kind === 'class' ? translate('venue_display.live.kind_class')
                : translate('venue_display.live.kind_event');
        return (
            <div className="vdl-time-col">
                <div className="vdl-time">{translate('venue_display.live.until', { time: formatTime(state.endsAt) })}</div>
                <div className="vdl-time-label">
                    {sportLabel} · {translate('venue_display.live.min_left', { count: minLeft })}
                </div>
            </div>
        );
    }
    if (state.kind === 'blocked') {
        return (
            <div className="vdl-time-col">
                <div className="vdl-time">{translate('venue_display.live.back_at', { time: formatTime(state.endsAt) })}</div>
                <div className="vdl-time-label">{state.reasonLabel}</div>
            </div>
        );
    }
    if (state.kind === 'open') {
        if (next) {
            return (
                <div className="vdl-time-col">
                    <div className="vdl-time">{translate('venue_display.live.starts_at', { time: formatTime(next.startsAt) })}</div>
                    <div className="vdl-time-label">
                        {translate('venue_display.live.in_min', { count: next.startsInMin })} · {next.label.slice(0, 36)}
                    </div>
                </div>
            );
        }
        return (
            <div className="vdl-time-col">
                <div className="vdl-time">{translate('venue_display.live.available')}</div>
                <div className="vdl-time-label">{translate('venue_display.live.no_bookings')}</div>
            </div>
        );
    }
    return (
        <div className="vdl-time-col">
            <div className="vdl-time">—</div>
            <div className="vdl-time-label">{translate('venue_display.live.idle')}</div>
        </div>
    );
}

function CourtRow({ court, now }: { court: VenueCourtState; now: Date }) {
    const isLive = court.current.kind === 'live' || court.current.kind === 'class' || court.current.kind === 'event';
    const cls = ['vdl-row'];
    if (isLive) cls.push('live');
    if (court.sticky) cls.push('sticky');
    const numCls = court.display.num.length > 3 ? 'vdl-num long' : 'vdl-num short';

    return (
        <div className={cls.join(' ')} data-sticky-label={translate('venue_display.live.starting_soon')}>
            <div className="vdl-num-col">
                <div className={numCls}>{court.display.num}</div>
                <div className="vdl-num-label">{court.display.label}</div>
            </div>
            <CurrentBlock state={court.current} />
            <TimeBlock state={court.current} next={court.next} now={now} />
        </div>
    );
}

function StackPages({ pages, now }: { pages: VenueCourtState[][]; now: Date }) {
    if (pages.length === 0) return null;
    if (pages.length === 1) {
        return (
            <div className="vdl-stack-page single">
                {pages[0].map((c) => <CourtRow key={c.courtId} court={c} now={now} />)}
            </div>
        );
    }
    return (
        <>
            <div className="vdl-stack-page p1">
                {pages[0].map((c) => <CourtRow key={c.courtId} court={c} now={now} />)}
            </div>
            <div className="vdl-stack-page p2">
                {(pages[1] ?? pages[0]).map((c) => <CourtRow key={c.courtId} court={c} now={now} />)}
            </div>
        </>
    );
}

/* ---- Main component ---- */

export default function VenueDisplayCalmLive() {
    const { courts, locationName, isLoading, hasError, needsLocation, syncedAt } = useVenueDisplayState();
    const { selectedOrganization } = useSelectedOrganization();
    const navigate = useNavigate();

    useEffect(() => {
        const fit = () => {
            const s = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
            document.documentElement.style.setProperty('--vdl-fit', s.toFixed(4));
        };
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);

    const [clockNow, setClockNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setClockNow(new Date()), 30_000);
        return () => clearInterval(id);
    }, []);

    const pages = useMemo(() => buildPages(courts), [courts]);
    const isStale = Date.now() - syncedAt.getTime() > STALE_MS;

    const headerTitle = selectedOrganization?.name ?? locationName ?? translate('venue_display.live.no_location');
    const headerCity = locationName && locationName !== headerTitle ? locationName : undefined;

    const welcomeMsg = courts.length === 1
        ? translate('venue_display.live.welcome_one')
        : translate('venue_display.live.welcome_other', { count: courts.length });

    const pageMsg = pages.length > 1
        ? translate('venue_display.live.page_rotation', { count: pages.length })
        : translate('venue_display.live.single_page');

    return (
        <div className="vdl-root">
            <a className="vdl-exit" href="#" onClick={(e) => { e.preventDefault(); navigate(-1); }}>
                {translate('venue_display.live.back')}
            </a>

            <div className="vdl-stage">
                <div className="vdl-head">
                    <div className="vdl-brand">
                        <span className="o">●</span>{headerTitle}
                        {headerCity && <span className="city">{headerCity}</span>}
                    </div>
                    <div className="vdl-clock">
                        {formatClock(clockNow)}
                        <span className="day">{formatDay(clockNow)}</span>
                    </div>
                </div>

                <div className={`vdl-progress${pages.length <= 1 ? ' single' : ''}`} aria-hidden="true" />

                {/*
                    `needsLocation` FIRST, and before the loading arm (run 134,
                    R110-6/R133-4). No club is selected, so this board will never
                    fill: the old code answered that with "no courts on this
                    venue's schedule", and answering it with the loading arm
                    instead would have spun forever, because React Query v4
                    reports a disabled query as `isLoading: true` with
                    `fetchStatus: 'idle'` (D64 — a permanent loading affordance
                    is strictly worse than the wrong claim it replaced).
                    `venue_display.live.no_location` is this file's own existing
                    string for exactly this condition; it was only ever used as a
                    subtitle fallback.
                */}
                {/*
                    The offline half (R134-4, run 135). `needsLocation` is
                    `state === 'never-issued'`, and a read the browser PAUSED for
                    want of a network lands there too — correctly, since nothing
                    was sent. Every other never-issued surface in the dashboard
                    gets the right sentence from `scopePromptI18nKey`; this board
                    uses its own string, so it is the one place that has to ask
                    the question itself. Without this it tells a venue's
                    reception screen to choose a club over a club that IS chosen.
                */}
                {needsLocation ? (
                    <div className="vdl-state">
                        <div className="h">
                            {translate(
                                isBrowserOffline()
                                    ? OFFLINE_NO_DATA_I18N_KEY as any
                                    : 'venue_display.live.no_location',
                            )}
                        </div>
                    </div>
                ) : isLoading && courts.length === 0 ? (
                    <div className="vdl-state">
                        <div className="h">{translate('venue_display.live.loading')}</div>
                        <div className="s">{locationName ?? translate('venue_display.live.no_location')}</div>
                    </div>
                ) : hasError ? (
                    <div className="vdl-state">
                        <div className="h">{translate('venue_display.live.error_title')}</div>
                        <div className="s">{translate('venue_display.live.error_sub')}</div>
                    </div>
                ) : courts.length === 0 ? (
                    <div className="vdl-state">
                        <div className="h">{translate('venue_display.live.no_courts_title')}</div>
                        <div className="s">{translate('venue_display.live.no_courts_sub')}</div>
                    </div>
                ) : (
                    <StackPages pages={pages} now={syncedAt} />
                )}

                <div className={`vdl-foot${isStale ? ' stale' : ''}`}>
                    <span>
                        <span className="ok" />
                        {isStale
                            ? translate('venue_display.live.last_sync', { seconds: Math.round((Date.now() - syncedAt.getTime()) / 1000) })
                            : welcomeMsg}
                    </span>
                    {pages.length > 1 ? (
                        <span className="vdl-pages">
                            <span className="pdot p1" />
                            <span className="pdot p2" />
                            <span className="plabel">{pageMsg}</span>
                        </span>
                    ) : (
                        <span className="vdl-pages">
                            <span className="pdot single" />
                            <span className="plabel">{pageMsg}</span>
                        </span>
                    )}
                    <span>{translate('venue_display.live.check_in')}</span>
                </div>
            </div>
        </div>
    );
}
